import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Copy, ExternalLink, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { UsernameEditor } from './UsernameEditor';

const API = '/api';

// UsernameSettingsModal - opened from the user dropdown menu. Lets the
// user pick / change their public-profile username, with live
// availability checks and a quick copy/preview of their profile URL.
export const UsernameSettingsModal = ({ open, onOpenChange }) => {
    const { user } = useAuth();
    // Live design version from the backend - drives the preview cache-buster
    // so a redeploy that bumps CARD_DESIGN_VERSION refreshes the preview
    // image even if the browser still has the old PNG cached. Initial
    // value is the build-time fallback; replaced as soon as the
    // /public/profiles/{me} fetch returns.
    const [cardVersion, setCardVersion] = useState('v3');

    useEffect(() => {
        if (open && user?.username) {
            // Pull the current design version so the preview shows the
            // image the backend actually has, not whatever was cached
            // from an earlier deploy. Best-effort; if it fails we keep
            // the build-time default.
            fetch(`${API}/public/profiles/${encodeURIComponent(user.username)}`)
                .then((r) => r.ok ? r.json() : null)
                .then((d) => { if (d?.card_version) setCardVersion(d.card_version); })
                .catch(() => {});
        }
    }, [open, user?.username]);

    const profileUrl = user?.username
        ? `${window.location.origin}/${user.username}`
        : '';
    // Rich-preview URL - backend-served HTML shell with per-user OG tags.
    // Hands social-share crawlers (Twitter, WhatsApp, Facebook, LinkedIn,
    // Discord, Slack, iMessage) a properly-tagged response so they render
    // the per-user OG card. Human visitors are redirected to the canonical
    // `/{username}` instantly via JS so the visible UX is unchanged.
    //
    // We append `?v={cardVersion}` to bust WhatsApp/Facebook's per-URL
    // preview cache whenever we redesign the OG card. Those platforms
    // cache the rendered preview against the exact shared URL for ~7
    // days regardless of Cache-Control headers, so without a version
    // bump every share continues showing the old broken preview to
    // recipients until the cache naturally expires. Each
    // CARD_DESIGN_VERSION bump in og_image.py changes the share URL,
    // which the social platforms treat as a brand-new link and
    // re-scrape from scratch.
    const shareUrl = user?.username
        ? `${window.location.origin}/api/p/${user.username}?v=${cardVersion}`
        : '';

    const copyProfileUrl = async () => {
        // Copy the rich-preview URL (`/api/p/{username}`) - pasting it on
        // any social platform fetches our backend HTML shell with
        // per-user OG/Twitter tags, producing a real preview card.
        // Pasting the canonical `/{username}` would still work for human
        // visitors but social previews would degrade to the generic
        // BrainSync card (SPA shell has no per-user meta tags in raw HTML).
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Share link copied - paste it on any social app for a rich preview');
        } catch (_) {
            toast.error('Could not copy link');
        }
    };

    const nativeShare = async () => {
        if (!navigator.share) {
            return copyProfileUrl();
        }
        try {
            await navigator.share({
                title: `${user?.name || user?.username}'s BrainSync`,
                text: 'Take a look at my public bookmarks on BrainSync.',
                url: shareUrl,
            });
        } catch (err) {
            // User cancelled the native share sheet - not an error worth toasting.
            if (err?.name !== 'AbortError') {
                toast.error('Could not open share sheet');
            }
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                /* `max-w-[calc(100vw-2rem)]` caps the dialog at viewport
                   width minus a 1rem gutter on each side. `min-w-0` on
                   the dialog AND its direct grid children is the
                   critical part: DialogContent uses `display:grid`,
                   and grid items default to `min-width:auto` (which
                   respects min-content). Without explicit `min-w-0`
                   on every grid child, the OG preview image's
                   intrinsic 1200px width forces the row track to
                   expand past the dialog cap, overflowing the screen.
                   See https://www.w3.org/TR/css-grid-1/#min-size-auto. */
                className="max-w-[calc(100vw-2rem)] sm:max-w-md min-w-0 max-h-[90vh] overflow-y-auto overflow-x-hidden"
                data-testid="username-settings-modal"
            >
                <DialogHeader className="min-w-0">
                    <DialogTitle>Your public profile</DialogTitle>
                    <DialogDescription>
                        This is the URL where people can find every bookmark and category you mark <b>Public</b>.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2 min-w-0">
                    {/* Social-share preview - the OG image rendered by the
                        backend at /api/og/profile/{username}.png. This is
                        exactly what Twitter/LinkedIn/WhatsApp/Discord
                        recipients see when the user shares their profile
                        URL. Showing it inline gives users confidence in
                        what gets posted and surfaces stale state too
                        (e.g. picture not yet synced). */}
                    {user?.username && (
                        <div className="space-y-1.5 min-w-0" data-testid="og-card-preview-block">
                            <div className="flex items-center justify-between min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Share preview
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                    How it looks when shared
                                </p>
                            </div>
                            {/* `aspect-[1200/630]` forces a fixed
                                aspect-ratio container so the image
                                can never push the modal wider than its
                                parent - `w-full` alone is unreliable
                                when an <img> reports its intrinsic
                                1200px width through certain flexbox
                                ancestors. The image is absolutely
                                positioned inside the box so its width
                                is irrelevant. `min-w-0` here is
                                defensive - if any grid/flex ancestor
                                forgets to set it, the 1200px image
                                blows the modal wide on mobile. */}
                            <div className="relative w-full min-w-0 aspect-[1200/630] rounded-xl border border-border overflow-hidden bg-white shadow-sm">
                                <img
                                    src={`/api/og/profile/${user.username}.png?v=${cardVersion}`}
                                    alt={`${user.name || user.username}'s social share card preview`}
                                    className="absolute inset-0 w-full h-full object-contain"
                                    data-testid="og-card-preview"
                                    onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = 'none'; }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Current profile URL (read-only display + copy + native share) */}
                    {user?.username && (
                        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-secondary border border-border">
                            <span className="text-xs text-muted-foreground font-mono truncate flex-1">
                                {profileUrl}
                            </span>
                            <button
                                type="button"
                                onClick={nativeShare}
                                className="text-emerald-700 hover:text-emerald-800 p-1"
                                data-testid="share-profile-btn"
                                title="Share with rich preview"
                            >
                                <Share2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={copyProfileUrl}
                                className="text-emerald-700 hover:text-emerald-800 p-1"
                                data-testid="copy-profile-url-btn"
                                title="Copy share link"
                            >
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                            <a
                                href={`/${user.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-700 hover:text-emerald-800 p-1"
                                data-testid="open-profile-link"
                                title="Open profile in new tab"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    )}

                    {/* Username editor - shared with /settings so the
                        modal and the Settings page always stay in sync. */}
                    <UsernameEditor
                        testIdPrefix="username-editor"
                        onSaved={() => onOpenChange(false)}
                    />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UsernameSettingsModal;
